/**
 * SEO Utility Functions for Visibeen
 * Comprehensive SEO helpers for meta tags, Open Graph, Twitter Cards, and more
 */

/**
 * Get site base URL
 * @returns {string} Base URL
 */
const getSiteBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'https://visibeen.com'; // Default base URL
};

/**
 * Generate page title with proper formatting
 * @param {string} title - Page specific title
 * @param {boolean} includeBrand - Whether to include brand name
 * @returns {string} Formatted page title
 */
export const generatePageTitle = (title, includeBrand = true) => {
  const brandName = 'Visibeen';
  const maxLength = 60; // Optimal title length for SEO

  if (!title) return brandName;

  const fullTitle = includeBrand ? `${title} | ${brandName}` : title;

  // Truncate if too long
  if (fullTitle.length > maxLength) {
    return fullTitle.substring(0, maxLength - 3) + '...';
  }

  return fullTitle;
};

/**
 * Generate meta description with proper length
 * @param {string} description - Page description
 * @returns {string} Optimized meta description
 */
export const generateMetaDescription = (description) => {
  const minLength = 120;
  const maxLength = 160; // Optimal meta description length

  if (!description) {
    return 'Create your professional business website with Visibeen. Get online presence with customizable templates, SEO optimization, and business tools.';
  }

  // Ensure minimum length
  if (description.length < minLength) {
    console.warn('Meta description is too short. Recommended: 120-160 characters');
  }

  // Truncate if too long
  if (description.length > maxLength) {
    return description.substring(0, maxLength - 3) + '...';
  }

  return description;
};

/**
 * Generate canonical URL
 * @param {string} path - Page path
 * @returns {string} Full canonical URL
 */
export const generateCanonicalUrl = (path = '') => {
  const baseUrl = getSiteBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Generate keywords from array
 * @param {Array<string>} keywords - Array of keywords
 * @returns {string} Comma-separated keywords
 */
export const generateKeywords = (keywords = []) => {
  const defaultKeywords = [
    'business website',
    'free website builder',
    'professional website',
    'online business',
    'website templates'
  ];

  const allKeywords = [...new Set([...keywords, ...defaultKeywords])];
  return allKeywords.join(', ');
};

/**
 * Generate Open Graph meta tags data
 * @param {Object} params - OG parameters
 * @returns {Object} Open Graph data object
 */
export const generateOpenGraphData = ({
  title,
  description,
  image,
  url,
  type = 'website',
  siteName = 'Visibeen'
}) => {
  const baseUrl = getSiteBaseUrl();

  return {
    'og:title': title,
    'og:description': description,
    'og:image': image || `${baseUrl}/og-image.png`,
    'og:url': url || (typeof window !== 'undefined' ? window.location.href : baseUrl),
    'og:type': type,
    'og:site_name': siteName,
    'og:locale': 'en_IN'
  };
};

/**
 * Generate Twitter Card meta tags data
 * @param {Object} params - Twitter Card parameters
 * @returns {Object} Twitter Card data object
 */
export const generateTwitterCardData = ({
  title,
  description,
  image,
  card = 'summary_large_image',
  site = '@visibeen',
  creator = '@visibeen'
}) => {
  const baseUrl = getSiteBaseUrl();

  return {
    'twitter:card': card,
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image || `${baseUrl}/twitter-card.png`,
    'twitter:site': site,
    'twitter:creator': creator
  };
};

/**
 * Generate breadcrumb keywords for SEO
 * @param {Array} breadcrumbs - Breadcrumb array
 * @returns {string} Breadcrumb keywords
 */
export const generateBreadcrumbKeywords = (breadcrumbs = []) => {
  return breadcrumbs.map(b => b.name).join(' > ');
};

/**
 * Generate location-based keywords
 * @param {string} city - City name
 * @param {string} state - State name
 * @param {string} category - Category name
 * @returns {Array<string>} Location keywords
 */
export const generateLocationKeywords = (city, state, category) => {
  const keywords = [];

  if (category) {
    keywords.push(
      category,
      `${category} in ${city}`,
      `${category} in ${state}`,
      `best ${category} in ${city}`,
      `top ${category} ${city}`,
      `${category} near me`
    );
  }

  if (city && state) {
    keywords.push(
      `${city} ${state}`,
      `businesses in ${city}`,
      `${city} directory`,
      `local services ${city}`
    );
  }

  return keywords;
};

/**
 * Generate FAQ schema markup
 * @param {Array} faqs - Array of FAQ objects {question, answer}
 * @returns {Object} FAQ Schema markup
 */
export const generateFAQSchema = (faqs = []) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

/**
 * Generate rating/review aggregate schema
 * @param {number} rating - Average rating
 * @param {number} reviewCount - Number of reviews
 * @returns {Object} AggregateRating schema
 */
export const generateAggregateRatingSchema = (rating, reviewCount) => {
  if (!rating || !reviewCount) return null;

  return {
    '@type': 'AggregateRating',
    ratingValue: rating,
    reviewCount: reviewCount,
    bestRating: 5,
    worstRating: 1
  };
};

/**
 * Sanitize text for SEO (remove special characters, normalize)
 * @param {string} text - Text to sanitize
 * @returns {string} Sanitized text
 */
export const sanitizeForSEO = (text) => {
  if (!text) return '';

  return text
    .replace(/[^\w\s-]/gi, '') // Remove special characters
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Generate slug from text
 * @param {string} text - Text to convert to slug
 * @returns {string} URL-friendly slug
 */
export const generateSlug = (text) => {
  if (!text) return '';

  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

/**
 * Check if URL is absolute
 * @param {string} url - URL to check
 * @returns {boolean} True if absolute URL
 */
export const isAbsoluteUrl = (url) => {
  return /^https?:\/\//i.test(url);
};

/**
 * Make URL absolute
 * @param {string} url - Relative or absolute URL
 * @returns {string} Absolute URL
 */
export const makeAbsoluteUrl = (url) => {
  if (!url) return '';
  if (isAbsoluteUrl(url)) return url;

  const baseUrl = getSiteBaseUrl();
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

/**
 * Generate complete SEO meta tags object
 * @param {Object} params - SEO parameters
 * @returns {Object} Complete meta tags object
 */
export const generateCompleteSEO = ({
  title,
  description,
  keywords = [],
  image,
  url,
  type = 'website',
  noindex = false,
  nofollow = false,
  canonical,
  author = 'Visibeen',
  publishedTime,
  modifiedTime
}) => {
  const pageTitle = generatePageTitle(title);
  const metaDescription = generateMetaDescription(description);
  const canonicalUrl = canonical || generateCanonicalUrl(url);
  const keywordString = generateKeywords(keywords);

  const robots = [];
  if (noindex) robots.push('noindex');
  if (nofollow) robots.push('nofollow');
  const robotsContent = robots.length > 0 ? robots.join(', ') : 'index, follow';

  const ogData = generateOpenGraphData({
    title: pageTitle,
    description: metaDescription,
    image,
    url: canonicalUrl,
    type
  });

  const twitterData = generateTwitterCardData({
    title: pageTitle,
    description: metaDescription,
    image
  });

  // Helpful defaults for richer previews
  const defaultSocialMeta = {
    ...(ogData['og:image'] && {
      'og:image:width': '1200',
      'og:image:height': '630'
    })
  };

  return {
    title: pageTitle,
    meta: {
      description: metaDescription,
      keywords: keywordString,
      author,
      robots: robotsContent,
      'google-site-verification': '', // Add your verification code
      viewport: 'width=device-width, initial-scale=1.0',
      'theme-color': '#0b91d6',
      ...(publishedTime && { 'article:published_time': publishedTime }),
      ...(modifiedTime && { 'article:modified_time': modifiedTime }),
      ...ogData,
      ...defaultSocialMeta,
      ...twitterData
    },
    link: {
      canonical: canonicalUrl
    }
  };
};

/**
 * Generate structured data for local business
 * @param {Object} business - Business data
 * @returns {Object} LocalBusiness schema
 */
export const generateLocalBusinessStructuredData = (business) => {
  const baseUrl = getSiteBaseUrl();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': business.businessType || 'LocalBusiness',
    '@id': `${baseUrl}/${business.slug}#business`,
    name: business.name,
    image: business.logo ? [makeAbsoluteUrl(business.logo)] : [],
    description: business.description || '',
    url: `${baseUrl}/${business.slug}`,
    telephone: business.phone || '',
    email: business.email || '',
    address: {
      '@type': 'PostalAddress',
      streetAddress: business.streetAddress || '',
      addressLocality: business.city || '',
      addressRegion: business.state || '',
      postalCode: business.postalCode || '',
      addressCountry: business.country || 'IN'
    }
  };

  // Add geo coordinates if available
  if (business.latitude && business.longitude) {
    structuredData.geo = {
      '@type': 'GeoCoordinates',
      latitude: business.latitude,
      longitude: business.longitude
    };

    // Add Plus Code if available
    if (business.plusCode) {
      structuredData.geo.plusCode = business.plusCode;
    }
  }

  // Add rating if available
  if (business.rating && business.reviewCount) {
    structuredData.aggregateRating = generateAggregateRatingSchema(
      business.rating,
      business.reviewCount
    );
  }

  // Add opening hours if available
  if (business.openingHours && business.openingHours.length > 0) {
    structuredData.openingHoursSpecification = business.openingHours.map(hours => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: hours.dayOfWeek,
      opens: hours.opens,
      closes: hours.closes
    }));
  }

  // Add social profiles
  if (business.socialProfiles && business.socialProfiles.length > 0) {
    structuredData.sameAs = business.socialProfiles.filter(Boolean);
  }

  // Add price range if available
  if (business.priceRange) {
    structuredData.priceRange = business.priceRange;
  }

  return structuredData;
};

/**
 * Generate schema for Free Website Template page
 * Used on /free-website and /live-demo pages
 * @param {Object} templateData - Template information
 * @returns {Object} Schema graph for template page
 */
export const generateWebsiteTemplateSchema = (templateData = {}) => {
  const baseUrl = getSiteBaseUrl();

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${baseUrl}/free-website#webpage`,
        'url': `${baseUrl}/free-website`,
        'name': 'Free Business Website Templates | Visibeen',
        'description': 'Create a professional business website for free with our beautiful templates. Sync with your Google Business Profile automatically.',
        'isPartOf': {
          '@id': `${baseUrl}/#website`
        },
        'about': {
          '@type': 'SoftwareApplication',
          '@id': `${baseUrl}/#website-builder`
        },
        'breadcrumb': {
          '@id': `${baseUrl}/free-website#breadcrumb`
        },
        'inLanguage': 'en-IN',
        'datePublished': '2023-01-01',
        'dateModified': new Date().toISOString().split('T')[0]
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${baseUrl}/#website-builder`,
        'name': 'Visibeen Free Website Builder',
        'applicationCategory': 'WebApplication',
        'applicationSubCategory': 'Website Builder',
        'operatingSystem': 'Web Browser',
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'INR',
          'availability': 'https://schema.org/InStock',
          'description': 'Free forever with premium upgrade options'
        },
        'featureList': [
          'Professional website templates',
          'Google Business Profile sync',
          'Mobile responsive design',
          'SEO optimized',
          'Custom domain support',
          'Contact form integration',
          'Business hours display',
          'Photo gallery',
          'Service showcase',
          'Customer reviews display'
        ],
        'screenshot': templateData.screenshot || `${baseUrl}/template-preview.png`,
        'softwareVersion': '2.0',
        'author': {
          '@id': `${baseUrl}/#organization`
        },
        'provider': {
          '@id': `${baseUrl}/#organization`
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.9',
          'ratingCount': '1250',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      {
        '@type': 'ItemList',
        '@id': `${baseUrl}/free-website#templates`,
        'name': 'Free Business Website Templates',
        'description': 'Choose from our collection of professionally designed website templates',
        'itemListOrder': 'https://schema.org/ItemListOrderAscending',
        'numberOfItems': templateData.templateCount || 8,
        'itemListElement': (templateData.templates || [
          { name: 'Classic Business', id: 'template1', description: 'Clean and professional design for any business' },
          { name: 'Modern Starter', id: 'template2', description: 'Contemporary design with bold typography' },
          { name: 'Elegant Pro', id: 'template3', description: 'Sophisticated layout for premium brands' },
          { name: 'Dynamic Portfolio', id: 'template4', description: 'Showcase your work with style' },
          { name: 'Service Focus', id: 'template5', description: 'Highlight your services effectively' },
          { name: 'Minimal Clean', id: 'template6', description: 'Simple and elegant minimalist design' },
          { name: 'Bold Impact', id: 'template7', description: 'Make a strong first impression' },
          { name: 'Professional Suite', id: 'template8', description: 'Complete business solution' }
        ]).map((template, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'item': {
            '@type': 'CreativeWork',
            '@id': `${baseUrl}/live-demo/${template.id}`,
            'name': template.name,
            'description': template.description,
            'url': `${baseUrl}/live-demo/${template.id}`,
            'creator': {
              '@id': `${baseUrl}/#organization`
            }
          }
        }))
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${baseUrl}/free-website#breadcrumb`,
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Home',
            'item': baseUrl
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': 'Free Website',
            'item': `${baseUrl}/free-website`
          }
        ]
      },
      {
        '@type': 'HowTo',
        '@id': `${baseUrl}/free-website#howto`,
        'name': 'How to Create a Free Business Website',
        'description': 'Create your professional business website in 3 simple steps',
        'step': [
          {
            '@type': 'HowToStep',
            'position': 1,
            'name': 'Connect Your Google Business Profile',
            'text': 'Sign in with your Google account and select your business profile to auto-import your business information.'
          },
          {
            '@type': 'HowToStep',
            'position': 2,
            'name': 'Choose a Template',
            'text': 'Browse our collection of professionally designed templates and select one that matches your brand.'
          },
          {
            '@type': 'HowToStep',
            'position': 3,
            'name': 'Customize and Publish',
            'text': 'Add your services, photos, and products, then publish your website instantly.'
          }
        ],
        'totalTime': 'PT5M',
        'tool': {
          '@type': 'HowToTool',
          'name': 'Visibeen Website Builder'
        }
      }
    ]
  };
};

/**
 * Generate schema for individual template demo/preview
 * @param {Object} template - Template data
 * @param {Object} business - Business data (if previewing with business)
 * @returns {Object} Schema for template demo page
 */
export const generateTemplateDemoSchema = (template = {}, business = null) => {
  const baseUrl = getSiteBaseUrl();
  const templateUrl = `${baseUrl}/live-demo/${template.id || 'template1'}`;

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${templateUrl}#webpage`,
        'url': templateUrl,
        'name': `${template.name || 'Website Template'} Preview | Visibeen`,
        'description': template.description || 'Preview this professional website template for your business.',
        'isPartOf': {
          '@id': `${baseUrl}/#website`
        },
        'breadcrumb': {
          '@id': `${templateUrl}#breadcrumb`
        },
        'inLanguage': 'en-IN'
      },
      {
        '@type': 'CreativeWork',
        '@id': `${templateUrl}#template`,
        'name': template.name || 'Business Website Template',
        'description': template.description || 'Professional website template for local businesses',
        'url': templateUrl,
        'creator': {
          '@id': `${baseUrl}/#organization`
        },
        'license': 'Free for use with Visibeen',
        'dateCreated': '2023-01-01',
        'image': template.thumbnail || `${baseUrl}/template-${template.id || '1'}.png`,
        'keywords': ['website template', 'business website', 'free template', 'professional design']
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${templateUrl}#breadcrumb`,
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Home',
            'item': baseUrl
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': 'Free Website',
            'item': `${baseUrl}/free-website`
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': template.name || 'Template Preview',
            'item': templateUrl
          }
        ]
      }
    ]
  };

  // If previewing with actual business data, add LocalBusiness schema
  if (business && business.name) {
    schema['@graph'].push({
      '@type': 'LocalBusiness',
      '@id': `${templateUrl}#business-preview`,
      'name': business.name,
      'description': business.description || `${business.name} - Professional business website`,
      'telephone': business.phone || undefined,
      'email': business.email || undefined,
      'address': business.streetAddress ? {
        '@type': 'PostalAddress',
        'streetAddress': business.streetAddress,
        'addressLocality': business.city || '',
        'addressRegion': business.state || '',
        'addressCountry': 'IN'
      } : undefined,
      'image': business.logo || undefined
    });
  }

  return schema;
};

/**
 * Generate schema for About Us page
 * @returns {Object} Schema for About page
 */
export const generateAboutPageSchema = () => {
  const baseUrl = getSiteBaseUrl();

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        '@id': `${baseUrl}/about-us#webpage`,
        'url': `${baseUrl}/about-us`,
        'name': 'About Visibeen | Our Story & Mission',
        'description': 'Learn about Visibeen - the leading Google Business Profile management platform helping businesses grow their online presence.',
        'isPartOf': {
          '@id': `${baseUrl}/#website`
        },
        'about': {
          '@id': `${baseUrl}/#organization`
        },
        'breadcrumb': {
          '@id': `${baseUrl}/about-us#breadcrumb`
        },
        'inLanguage': 'en-IN',
        'mainEntity': {
          '@id': `${baseUrl}/#organization`
        }
      },
      {
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        'name': 'Visibeen',
        'legalName': 'Visibeen Technologies Private Limited',
        'url': baseUrl,
        'logo': `${baseUrl}/visibeen-logo.svg`,
        'description': 'Visibeen is a comprehensive Google Business Profile management platform that helps businesses improve their local SEO, manage reviews, and attract more customers.',
        'foundingDate': '2023',
        'founder': {
          '@type': 'Person',
          'name': 'Visibeen Team'
        },
        'knowsAbout': [
          'Google Business Profile Management',
          'Local SEO',
          'Business Website Builder',
          'Review Management',
          'Competitor Analysis'
        ],
        'slogan': 'Grow Your Business with Better Online Presence'
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${baseUrl}/about-us#breadcrumb`,
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': baseUrl },
          { '@type': 'ListItem', 'position': 2, 'name': 'About Us', 'item': `${baseUrl}/about-us` }
        ]
      }
    ]
  };
};

/**
 * Generate schema for Contact Us page
 * @param {Object} contactInfo - Contact information
 * @returns {Object} Schema for Contact page
 */
export const generateContactPageSchema = (contactInfo = {}) => {
  const baseUrl = getSiteBaseUrl();

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ContactPage',
        '@id': `${baseUrl}/contact-us#webpage`,
        'url': `${baseUrl}/contact-us`,
        'name': 'Contact Visibeen | Get in Touch',
        'description': 'Contact the Visibeen team for support, sales inquiries, or partnership opportunities. We are here to help you grow your business.',
        'isPartOf': {
          '@id': `${baseUrl}/#website`
        },
        'breadcrumb': {
          '@id': `${baseUrl}/contact-us#breadcrumb`
        },
        'inLanguage': 'en-IN',
        'mainEntity': {
          '@type': 'Organization',
          '@id': `${baseUrl}/#organization`,
          'name': 'Visibeen',
          'email': contactInfo.email || 'support@visibeen.com',
          'contactPoint': [
            {
              '@type': 'ContactPoint',
              'contactType': 'customer service',
              'email': contactInfo.supportEmail || 'support@visibeen.com',
              'availableLanguage': ['English', 'Hindi']
            },
            {
              '@type': 'ContactPoint',
              'contactType': 'sales',
              'email': contactInfo.salesEmail || 'sales@visibeen.com',
              'availableLanguage': ['English', 'Hindi']
            }
          ]
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${baseUrl}/contact-us#breadcrumb`,
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': baseUrl },
          { '@type': 'ListItem', 'position': 2, 'name': 'Contact Us', 'item': `${baseUrl}/contact-us` }
        ]
      }
    ]
  };
};

/**
 * Generate schema for Pricing page
 * @param {Array} plans - Array of pricing plans
 * @returns {Object} Schema for Pricing page
 */
export const generatePricingPageSchema = (plans = []) => {
  const baseUrl = getSiteBaseUrl();

  const defaultPlans = plans.length > 0 ? plans : [
    { name: 'Free', price: 0, description: 'Essential features for getting started', features: ['1 Business Profile', 'Basic Analytics', 'Free Website'] },
    { name: 'Starter', price: 499, description: 'For growing businesses', features: ['3 Business Profiles', 'Advanced Analytics', 'Priority Support'] },
    { name: 'Professional', price: 1499, description: 'For established businesses', features: ['10 Business Profiles', 'Competitor Analysis', 'White-label Options'] },
    { name: 'Enterprise', price: 4999, description: 'For agencies and large businesses', features: ['Unlimited Profiles', 'API Access', 'Dedicated Support'] }
  ];

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${baseUrl}/pricing#webpage`,
        'url': `${baseUrl}/pricing`,
        'name': 'Pricing Plans | Visibeen',
        'description': 'Choose the perfect plan for your business. From free essential features to enterprise solutions.',
        'isPartOf': {
          '@id': `${baseUrl}/#website`
        },
        'breadcrumb': {
          '@id': `${baseUrl}/pricing#breadcrumb`
        },
        'inLanguage': 'en-IN'
      },
      {
        '@type': 'Product',
        '@id': `${baseUrl}/#product`,
        'name': 'Visibeen GBP Management Platform',
        'description': 'Comprehensive Google Business Profile management solution with multiple pricing tiers',
        'brand': {
          '@id': `${baseUrl}/#organization`
        },
        'offers': {
          '@type': 'AggregateOffer',
          'lowPrice': '0',
          'highPrice': '4999',
          'priceCurrency': 'INR',
          'offerCount': defaultPlans.length.toString(),
          'offers': defaultPlans.map((plan, index) => ({
            '@type': 'Offer',
            '@id': `${baseUrl}/pricing#${plan.name.toLowerCase()}`,
            'name': `${plan.name} Plan`,
            'description': plan.description,
            'price': plan.price.toString(),
            'priceCurrency': 'INR',
            'priceValidUntil': new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
            'availability': 'https://schema.org/InStock',
            'url': `${baseUrl}/pricing#${plan.name.toLowerCase()}`,
            'seller': {
              '@id': `${baseUrl}/#organization`
            }
          }))
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '500',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${baseUrl}/pricing#breadcrumb`,
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': baseUrl },
          { '@type': 'ListItem', 'position': 2, 'name': 'Pricing', 'item': `${baseUrl}/pricing` }
        ]
      }
    ]
  };
};

/**
 * Generate optimized image alt text
 * @param {string} businessName - Business name
 * @param {string} imageContext - Context of the image (e.g., "logo", "service", "interior")
 * @param {string} city - City name
 * @param {string} category - Business category
 * @returns {string} Optimized alt text
 */
export const generateImageAlt = (businessName, imageContext = '', city = '', category = '') => {
  const parts = [];

  if (imageContext) {
    parts.push(imageContext);
  }

  if (businessName) {
    parts.push(businessName);
  }

  if (category) {
    parts.push(category);
  }

  if (city) {
    parts.push(`in ${city}`);
  }

  return parts.join(' - ');
};

export default {
  generatePageTitle,
  generateMetaDescription,
  generateCanonicalUrl,
  generateKeywords,
  generateOpenGraphData,
  generateTwitterCardData,
  generateBreadcrumbKeywords,
  generateLocationKeywords,
  generateFAQSchema,
  generateAggregateRatingSchema,
  sanitizeForSEO,
  generateSlug,
  isAbsoluteUrl,
  makeAbsoluteUrl,
  generateCompleteSEO,
  generateLocalBusinessStructuredData,
  generateImageAlt,
  // New schema functions for pages
  generateWebsiteTemplateSchema,
  generateTemplateDemoSchema,
  generateAboutPageSchema,
  generateContactPageSchema,
  generatePricingPageSchema
};
